import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

const CACHE_DIR = 'media_cache';
const INDEX_FILE = 'media_cache_index.json';
const MAX_CACHE_ENTRIES = 200;

interface CacheIndex {
  [proxyUrl: string]: { filename: string; timestamp: number };
}

let indexLoaded = false;
let cacheIndex: CacheIndex = {};

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

function urlToFilename(url: string): string {
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = ((hash << 5) - hash + url.charCodeAt(i)) | 0;
  }
  return `img_${Math.abs(hash).toString(36)}.bin`;
}

async function loadIndex(): Promise<void> {
  if (indexLoaded) return;
  try {
    const result = await Filesystem.readFile({
      path: `${CACHE_DIR}/${INDEX_FILE}`,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    cacheIndex = JSON.parse(result.data as string);
  } catch {
    cacheIndex = {};
  }
  indexLoaded = true;
}

async function saveIndex(): Promise<void> {
  try {
    await Filesystem.writeFile({
      path: `${CACHE_DIR}/${INDEX_FILE}`,
      data: JSON.stringify(cacheIndex),
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
      recursive: true,
    });
  } catch (e) {
    console.warn('[MediaCache] Failed to save index', e);
  }
}

async function evictOldest(): Promise<void> {
  const entries = Object.entries(cacheIndex);
  if (entries.length <= MAX_CACHE_ENTRIES) return;
  entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
  const toRemove = entries.slice(0, entries.length - MAX_CACHE_ENTRIES);
  for (const [url, { filename }] of toRemove) {
    try {
      await Filesystem.deleteFile({ path: `${CACHE_DIR}/${filename}`, directory: Directory.Cache });
    } catch { /* already gone */ }
    delete cacheIndex[url];
  }
}

/**
 * Try to read a cached image. Returns a blob URL if cached, null otherwise.
 */
export async function getCachedMedia(proxyUrl: string): Promise<string | null> {
  if (!isNative()) return null;
  try {
    await loadIndex();
    const entry = cacheIndex[proxyUrl];
    if (!entry) return null;

    const result = await Filesystem.readFile({
      path: `${CACHE_DIR}/${entry.filename}`,
      directory: Directory.Cache,
    });

    // result.data is a base64 string on native
    const binary = atob(result.data as string);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'image/jpeg' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Write a fetched blob to the persistent filesystem cache.
 */
export async function cacheMedia(proxyUrl: string, blob: Blob): Promise<void> {
  if (!isNative()) return;
  try {
    await loadIndex();
    const filename = urlToFilename(proxyUrl);

    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const base64 = btoa(binary);

    await Filesystem.writeFile({
      path: `${CACHE_DIR}/${filename}`,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });

    cacheIndex[proxyUrl] = { filename, timestamp: Date.now() };
    await evictOldest();
    await saveIndex();
  } catch (e) {
    console.warn('[MediaCache] Failed to cache media', e);
  }
}
